/**
 * Interactive MedMic sign-in that always works, without clearing site data.
 *
 * Used by "Sign in again" (Unable to connect MedMic) and as the fallback of
 * "Connect MedMic" when the silent connect fails. Recovers from the states that
 * used to require clearing site data:
 *  - a stale MSAL "interaction in progress" flag (e.g. an aborted redirect),
 *  - an inconsistent MSAL cache (loginRedirect throws for any other reason).
 */
import { BrowserAuthErrorCodes } from "@azure/msal-browser";
import { creLoginRequest } from "./creAuthConfig";
import { getCreActiveAccount } from "./creAccountUtils";
import { setCreLoginIntent } from "./creLoginIntent";
import { setMedmicReturnRoute } from "./creMedmicReturnRoute";
import { markMedMicExplicitConnect } from "./medmicExplicitConnect";
import { CRE_EXTENSION_PATH } from "../constants/routes";

/** MSAL v3 keeps the interaction flag in sessionStorage under this key. */
export const MSAL_INTERACTION_STATUS_KEY = "msal.interaction.status";

function clearStaleInteractionFlag() {
  try {
    sessionStorage.removeItem(MSAL_INTERACTION_STATUS_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * @param {import("@azure/msal-browser").IPublicClientApplication} instance
 * @param {{ returnRoute?: string }} [opts]
 * @returns {Promise<"redirecting" | "failed">}
 */
export async function startMedmicInteractiveSignIn(instance, opts = {}) {
  setCreLoginIntent("extension");
  setMedmicReturnRoute(opts.returnRoute || CRE_EXTENSION_PATH);
  markMedMicExplicitConnect();

  let account = null;
  try {
    account = getCreActiveAccount(instance);
  } catch {
    account = null;
  }
  const request = {
    ...creLoginRequest,
    // Same person signs in again: pre-fill the account, still allow switching.
    ...(account?.username ? { loginHint: account.username } : {}),
  };

  // The user clicked: any recorded "interaction in progress" is stale.
  clearStaleInteractionFlag();
  try {
    await instance.loginRedirect(request);
    return "redirecting";
  } catch (err) {
    const code = err?.errorCode || "";
    if (code === BrowserAuthErrorCodes.interactionInProgress || code === "interaction_in_progress") {
      clearStaleInteractionFlag();
    } else {
      // Inconsistent cache: start clean for this app (only MSAL's own entries).
      try {
        await instance.clearCache();
      } catch {
        /* ignore */
      }
      clearStaleInteractionFlag();
    }
    try {
      await instance.loginRedirect({ ...creLoginRequest });
      return "redirecting";
    } catch (retryErr) {
      console.error("[MedMic] interactive sign-in failed", retryErr?.errorCode || retryErr);
      return "failed";
    }
  }
}
